"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useEmoji } from "@/contexts/EmojiContext";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import type { ApiAddReactionToDropRequest } from "@/generated/models/ApiAddReactionToDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropContextProfileContext } from "@/generated/models/ApiDropContextProfileContext";
import type { ApiDropReaction } from "@/generated/models/ApiDropReaction";
import { formatLargeNumber } from "@/helpers/Helpers";
import { recordReaction } from "@/helpers/reactions/reactionHistory";
import { buildTooltipId } from "@/helpers/tooltip.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { commonApiDelete, commonApiPost } from "@/services/api/common-api";
import { useWebsocketStatus } from "@/services/websocket/useWebSocketMessage";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { cloneReactionEntries, applyProfileReactionToEntries, getReactionErrorMessage, getReactionCount, removeUserFromReactions, toProfileMin } from "./reaction-utils";
import { beginReactionMutation, deriveReactionAction, recordReactionOptimisticApplied, recordReactionRequestFailed, recordReactionRequestSent, recordReactionRequestSucceeded, recordReactionRollbackApplied } from "@/utils/monitoring/dropReactionMonitoring";
import styles from "./WaveDropReactions.module.scss";
import WaveDropReactionsDetailDialog from "./WaveDropReactionsDetailDialog";
import { fetchDropReactionDetailsV2 } from "@/services/api/wave-drops-v2-api";
interface WaveDropReactionsProps {
  readonly drop: ApiDrop;
}
interface DetailedReactionsState {
  readonly dropId: string;
  readonly reactions: ApiDropReaction[];
}
const getReactionClassNames = ({
  animate,
  canReact,
  selected
}: {
  readonly animate: boolean;
  readonly canReact: boolean;
  readonly selected: boolean;
}) => {
  let hoverStyle = "";
  if (canReact) {
    hoverStyle = selected ? "hover:tw-border-primary-500 hover:tw-bg-primary-500/10" : "hover:tw-border-iron-500 hover:tw-bg-iron-900/40";
  }
  let animationStyle = "";
  if (animate) {
    animationStyle = selected ? styles["reactionSlideUp"]! : styles["reactionSlideDown"]!;
  }
  return {
    borderStyle: selected ? "tw-border-primary-500" : "tw-border-iron-700",
    bgStyle: selected ? "tw-bg-primary-500/10" : "tw-bg-iron-900/40",
    hoverStyle,
    animationStyle
  };
};
function ReactionTooltipContent(t0) {
  const $ = _c(16);
  const {
    displayProfiles,
    isDetailsLoading,
    moreCount,
    onMoreClick,
    total
  } = t0;
  if (isDetailsLoading && displayProfiles.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="tw-whitespace-nowrap">Loading reactions...</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (displayProfiles.length === 0) {
    let t1;
    if ($[1] !== total) {
      t1 = formatLargeNumber(total);
      $[1] = total;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const t2 = total === 1 ? "reaction" : "reactions";
    let t3;
    if ($[3] !== t1 || $[4] !== t2) {
      t3 = <span className="tw-whitespace-nowrap">{t1} {t2}</span>;
      $[3] = t1;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t1;
  if ($[6] !== displayProfiles) {
    let t2;
    if ($[8] !== displayProfiles.length) {
      t2 = (profile, index) => {
        const displayName = profile.handle ?? profile.id;
        const isLast = index === displayProfiles.length - 1;
        return <span key={profile.id}>{profile.handle ? <Link href={`/${profile.handle}`} className="tw-text-primary-400 tw-no-underline hover:tw-text-primary-300 hover:tw-underline" onClick={_temp}>{displayName}</Link> : <span>{displayName}</span>}{isLast ? null : ", "}</span>;
      };
      $[8] = displayProfiles.length;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t1 = displayProfiles.map(t2);
    $[6] = displayProfiles;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[10] !== moreCount || $[11] !== onMoreClick) {
    t2 = moreCount > 0 && <>{" "}<button type="button" onClick={onMoreClick} className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-primary-400 tw-underline hover:tw-text-primary-300">and {moreCount} {moreCount === 1 ? "other" : "others"}</button></>;
    $[10] = moreCount;
    $[11] = onMoreClick;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t1 || $[14] !== t2) {
    t3 = <span className="tw-whitespace-nowrap">by{" "}{t1}{t2}</span>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
function _temp(e) {
  return e.stopPropagation();
}
const WaveDropReactions: React.FC<WaveDropReactionsProps> = ({
  drop
}) => {
  const [dialogReaction, setDialogReaction] = useState<string | null>(null);
  const [detailedReactionsState, setDetailedReactionsState] = useState<DetailedReactionsState | null>(null);
  const [detailsLoadingDropId, setDetailsLoadingDropId] = useState<string | null>(null);
  const detailsRequestRef = useRef<{
    readonly dropId: string;
    readonly promise: Promise<void>;
  } | null>(null);
  const isTouchDevice = useIsTouchDevice();
  const detailedReactions = detailedReactionsState?.dropId === drop.id ? detailedReactionsState.reactions : null;
  const detailsLoading = detailsLoadingDropId === drop.id;
  const reactionsWithDetails = useMemo(() => {
    if (!detailedReactions) {
      return drop.reactions;
    }
    const detailsByReaction = new Map(detailedReactions.map(reaction => [reaction.reaction, reaction]));
    return drop.reactions.map(reaction_0 => {
      const detailedReaction = detailsByReaction.get(reaction_0.reaction);
      if (!detailedReaction) {
        return reaction_0;
      }
      const profilesById = new Map(detailedReaction.profiles.map(profile => [profile.id, profile]));
      for (const profile_0 of reaction_0.profiles) {
        profilesById.set(profile_0.id, profile_0);
      }
      return {
        ...reaction_0,
        profiles: [...profilesById.values()],
        count: getReactionCount(reaction_0)
      };
    });
  }, [detailedReactions, drop.reactions]);
  const loadReactionDetails = useCallback(() => {
    if (detailedReactions) {
      return null;
    }
    if (detailsRequestRef.current?.dropId === drop.id) {
      return detailsRequestRef.current.promise;
    }
    const requestDropId = drop.id;
    const request = (async () => {
      setDetailsLoadingDropId(requestDropId);
      try {
        const reactions = await fetchDropReactionDetailsV2(requestDropId);
        setDetailedReactionsState({
          dropId: requestDropId,
          reactions
        });
      } catch {
        setDetailedReactionsState({
          dropId: requestDropId,
          reactions: []
        });
      } finally {
        setDetailsLoadingDropId(current => current === requestDropId ? null : current);
        detailsRequestRef.current = null;
      }
    })();
    detailsRequestRef.current = {
      dropId: requestDropId,
      promise: request
    };
    return request;
  }, [detailedReactions, drop.id]);
  const handleOpenDialog = useCallback((reactionKey: string) => {
    setDialogReaction(reactionKey);
    loadReactionDetails()?.catch(() => undefined);
  }, [loadReactionDetails]);
  const handleCloseDialog = useCallback(() => {
    setDialogReaction(null);
  }, []);
  return <>
      {reactionsWithDetails.map(reaction_1 => <WaveDropReaction key={`${reaction_1.reaction}-${getReactionCount(reaction_1)}`} drop={drop} reaction={reaction_1} onOpenDetailDialog={handleOpenDialog} onLoadDetails={loadReactionDetails} isDetailsLoading={detailsLoading} isTouchDevice={isTouchDevice} />)}
      <WaveDropReactionsDetailDialog isOpen={dialogReaction !== null} onClose={handleCloseDialog} reactions={reactionsWithDetails} initialReaction={dialogReaction ?? undefined} isLoading={detailsLoading} />
    </>;
};
function WaveDropReaction(t0) {
  const $ = _c(154);
  const {
    drop,
    reaction,
    onOpenDetailDialog,
    onLoadDetails,
    isDetailsLoading,
    isTouchDevice
  } = t0;
  const {
    setToast,
    connectedProfile
  } = useAuth();
  const {
    emojiMap,
    findNativeEmoji
  } = useEmoji();
  const {
    applyOptimisticDropUpdate
  } = useMyStream();
  const websocketStatus = useWebsocketStatus();
  const rollbackRef = useRef(null);
  const canReact = Boolean(connectedProfile?.handle);
  let t1;
  if ($[0] !== onOpenDetailDialog || $[1] !== reaction.reaction) {
    t1 = () => {
      onOpenDetailDialog(reaction.reaction);
    };
    $[0] = onOpenDetailDialog;
    $[1] = reaction.reaction;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleLongPressStart = t1;
  let t2;
  if ($[3] !== handleLongPressStart || $[4] !== isTouchDevice) {
    t2 = {
      hasTouchScreen: isTouchDevice,
      onInteractionStart: handleLongPressStart,
      longPressDuration: 400,
      preventDefault: false
    };
    $[3] = handleLongPressStart;
    $[4] = isTouchDevice;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    longPressTriggered,
    touchHandlers
  } = useLongPressInteraction(t2);
  let t3;
  if ($[6] !== touchHandlers) {
    t3 = {
      onTouchStart: e => {
        e.stopPropagation();
        touchHandlers.onTouchStart(e);
      },
      onTouchMove: e_0 => {
        e_0.stopPropagation();
        touchHandlers.onTouchMove(e_0);
      },
      onTouchEnd: e_1 => {
        e_1.stopPropagation();
        touchHandlers.onTouchEnd();
      },
      onTouchCancel: e_2 => {
        e_2.stopPropagation();
        touchHandlers.onTouchCancel();
      }
    };
    $[6] = touchHandlers;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const wrappedTouchHandlers = t3;
  let t4;
  if ($[8] !== reaction) {
    t4 = getReactionCount(reaction);
    $[8] = reaction;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const [total, setTotal] = useState(t4);
  const [selected, setSelected] = useState(reaction.reaction === drop.context_profile_context?.reaction);
  const [animate, setAnimate] = useState(false);
  const prevTotalRef = useRef(total);
  const prevContextReactionRef = useRef(drop.context_profile_context?.reaction);
  const prevProfilesRef = useRef(reaction.profiles);
  let t5;
  if ($[10] !== drop.context_profile_context?.reaction || $[11] !== reaction.reaction) {
    t5 = () => {
      if (drop.context_profile_context?.reaction === prevContextReactionRef.current) {
        return;
      }
      prevContextReactionRef.current = drop.context_profile_context?.reaction;
      const isSelected = reaction.reaction === drop.context_profile_context?.reaction;
      const timeoutId = setTimeout(() => {
        setSelected(current => current === isSelected ? current : isSelected);
      }, 0);
      return () => clearTimeout(timeoutId);
    };
    $[10] = drop.context_profile_context?.reaction;
    $[11] = reaction.reaction;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = drop.context_profile_context?.reaction;
  let t7;
  if ($[13] !== reaction.reaction || $[14] !== t6) {
    t7 = [t6, reaction.reaction];
    $[13] = reaction.reaction;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  useEffect(t5, t7);
  let t8;
  let t9;
  if ($[16] !== reaction) {
    t8 = () => {
      const nextTotal = getReactionCount(reaction);
      if (reaction.profiles === prevProfilesRef.current) {
        const timeoutId_0 = setTimeout(() => {
          setTotal(current_0 => current_0 === nextTotal ? current_0 : nextTotal);
        }, 0);
        return () => clearTimeout(timeoutId_0);
      }
      prevProfilesRef.current = reaction.profiles;
      const timeoutId_1 = setTimeout(() => {
        setTotal(current_1 => current_1 === nextTotal ? current_1 : nextTotal);
      }, 0);
      return () => clearTimeout(timeoutId_1);
    };
    t9 = [reaction];
    $[16] = reaction;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[19] !== total) {
    t10 = () => {
      if (total === prevTotalRef.current) {
        return;
      }
      prevTotalRef.current = total;
      const timeoutId_2 = setTimeout(() => setAnimate(true), 0);
      return () => clearTimeout(timeoutId_2);
    };
    t11 = [total];
    $[19] = total;
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[22] !== animate) {
    t12 = () => {
      if (!animate) {
        return;
      }
      const timeout = setTimeout(() => setAnimate(false), 100);
      return () => clearTimeout(timeout);
    };
    t13 = [animate];
    $[22] = animate;
    $[23] = t12;
    $[24] = t13;
  } else {
    t12 = $[23];
    t13 = $[24];
  }
  useEffect(t12, t13);
  let t14;
  if ($[25] !== reaction.reaction) {
    t14 = reaction.reaction.replaceAll(":", "");
    $[25] = reaction.reaction;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const emojiId = t14;
  let t15;
  if ($[27] !== drop.id || $[28] !== emojiId) {
    t15 = buildTooltipId("reaction", drop.id, emojiId);
    $[27] = drop.id;
    $[28] = emojiId;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const tooltipId = t15;
  const waveId = drop.wave.id;
  let t16;
  bb0: {
    let t17;
    if ($[30] !== emojiId || $[31] !== emojiMap) {
      let t18;
      if ($[33] !== emojiId) {
        t18 = e_3 => e_3.id === emojiId;
        $[33] = emojiId;
        $[34] = t18;
      } else {
        t18 = $[34];
      }
      t17 = emojiMap.flatMap(_temp2).find(t18);
      $[30] = emojiId;
      $[31] = emojiMap;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    const custom = t17;
    const customSrc = custom?.skins[0]?.src;
    if (customSrc) {
      let t18;
      if ($[35] !== customSrc || $[36] !== emojiId) {
        t18 = <div className="tw-relative tw-size-4"><Image src={customSrc} alt={emojiId} fill={true} className="tw-object-contain" /></div>;
        $[35] = customSrc;
        $[36] = emojiId;
        $[37] = t18;
      } else {
        t18 = $[37];
      }
      let t19;
      if ($[38] !== customSrc || $[39] !== emojiId) {
        t19 = <div className="tw-relative tw-size-8"><Image src={customSrc} alt={emojiId} fill={true} className="tw-rounded-sm tw-object-contain" /></div>;
        $[38] = customSrc;
        $[39] = emojiId;
        $[40] = t19;
      } else {
        t19 = $[40];
      }
      let t20;
      if ($[41] !== t18 || $[42] !== t19) {
        t20 = {
          emojiNode: t18,
          emojiNodeTooltip: t19
        };
        $[41] = t18;
        $[42] = t19;
        $[43] = t20;
      } else {
        t20 = $[43];
      }
      t16 = t20;
      break bb0;
    }
    let t18;
    if ($[44] !== emojiId || $[45] !== findNativeEmoji) {
      t18 = findNativeEmoji(emojiId);
      $[44] = emojiId;
      $[45] = findNativeEmoji;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    const native = t18;
    if (native) {
      const t19 = native.skins[0]?.native;
      let t20;
      if ($[47] !== t19) {
        t20 = <span className="tw-flex tw-items-center tw-justify-center tw-text-[1rem]">{t19}</span>;
        $[47] = t19;
        $[48] = t20;
      } else {
        t20 = $[48];
      }
      const t21 = native.skins[0]?.native;
      let t22;
      if ($[49] !== t21) {
        t22 = <span className="tw-flex tw-items-center tw-justify-center tw-text-2xl">{t21}</span>;
        $[49] = t21;
        $[50] = t22;
      } else {
        t22 = $[50];
      }
      let t23;
      if ($[51] !== t20 || $[52] !== t22) {
        t23 = {
          emojiNode: t20,
          emojiNodeTooltip: t22
        };
        $[51] = t20;
        $[52] = t22;
        $[53] = t23;
      } else {
        t23 = $[53];
      }
      t16 = t23;
      break bb0;
    }
    let t19;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = {
        emojiNode: null,
        emojiNodeTooltip: null
      };
      $[54] = t19;
    } else {
      t19 = $[54];
    }
    t16 = t19;
  }
  const {
    emojiNode,
    emojiNodeTooltip
  } = t16;
  let t17;
  if ($[55] !== applyOptimisticDropUpdate || $[56] !== connectedProfile || $[57] !== drop.context_profile_context || $[58] !== drop.id || $[59] !== reaction.reaction || $[60] !== waveId) {
    t17 = willSelect => {
      if (!waveId) {
        return;
      }
      const userProfileMin = toProfileMin(connectedProfile);
      rollbackRef.current = applyOptimisticDropUpdate({
        waveId,
        dropId: drop.id,
        update: draft => {
          if (draft.type !== DropSize.FULL) {
            return draft;
          }
          const reactions = cloneReactionEntries(draft.reactions);
          const userId = connectedProfile?.id ?? null;
          draft.reactions = userProfileMin ? applyProfileReactionToEntries({
            entries: reactions,
            nextReaction: willSelect ? reaction.reaction : null,
            previousReaction: drop.context_profile_context?.reaction ?? null,
            profileMin: userProfileMin
          }) : removeUserFromReactions(reactions, userId);
          const existingContext = draft.context_profile_context ?? drop.context_profile_context ?? {
            rating: 0,
            min_rating: 0,
            max_rating: 0,
            reaction: null,
            boosted: false,
            bookmarked: false,
            curatable: false,
            curated: false
          };
          draft.context_profile_context = {
            ...existingContext,
            reaction: willSelect ? reaction.reaction : null
          };
          return draft;
        }
      })?.rollback ?? null;
    };
    $[55] = applyOptimisticDropUpdate;
    $[56] = connectedProfile;
    $[57] = drop.context_profile_context;
    $[58] = drop.id;
    $[59] = reaction.reaction;
    $[60] = waveId;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  const applyOptimisticReactionChange = t17;
  let t18;
  if ($[62] !== applyOptimisticReactionChange || $[63] !== canReact || $[64] !== connectedProfile?.id || $[65] !== drop.context_profile_context?.reaction || $[66] !== drop.id || $[67] !== longPressTriggered || $[68] !== reaction.reaction || $[69] !== selected || $[70] !== setToast || $[71] !== waveId || $[72] !== websocketStatus) {
    t18 = async () => {
      if (!canReact || longPressTriggered) {
        return;
      }
      const intendedReaction = selected ? null : reaction.reaction;
      const mutation = beginReactionMutation({
        dropId: drop.id,
        waveId,
        source: "chip",
        action: deriveReactionAction(drop.context_profile_context?.reaction ?? null, intendedReaction),
        previousReaction: drop.context_profile_context?.reaction ?? null,
        intendedReaction,
        optimisticReaction: intendedReaction,
        profileId: connectedProfile?.id ?? null,
        websocketStatus
      });
      setSelected(_temp3);
      setTotal(n => Math.max(0, n + (selected ? -1 : 1)));
      applyOptimisticReactionChange(!selected);
      recordReactionOptimisticApplied(mutation);
      if (!selected) {
        recordReaction(reaction.reaction);
      }
      ;
      try {
        const body = {
          reaction: reaction.reaction
        };
        const endpoint = `drops/${drop.id}/reaction`;
        if (selected) {
          recordReactionRequestSent(mutation, {
            endpoint,
            method: "DELETE"
          });
          await commonApiDelete({
            endpoint,
            errorMode: "structured"
          });
        } else {
          recordReactionRequestSent(mutation, {
            endpoint,
            method: "POST"
          });
          await commonApiPost({
            endpoint,
            body,
            errorMode: "structured"
          });
        }
        recordReactionRequestSucceeded(mutation);
      } catch (t19) {
        const error = t19;
        recordReactionRequestFailed(mutation, error);
        const msg = getReactionErrorMessage(error, selected ? "Error removing reaction" : "Error adding reaction");
        setToast({
          message: msg,
          type: "error"
        });
        setSelected(_temp4);
        setTotal(n_0 => Math.max(0, n_0 + (selected ? 1 : -1)));
        rollbackRef.current?.();
        recordReactionRollbackApplied(mutation);
        rollbackRef.current = null;
      }
      rollbackRef.current = null;
    };
    $[62] = applyOptimisticReactionChange;
    $[63] = canReact;
    $[64] = connectedProfile?.id;
    $[65] = drop.context_profile_context?.reaction;
    $[66] = drop.id;
    $[67] = longPressTriggered;
    $[68] = reaction.reaction;
    $[69] = selected;
    $[70] = setToast;
    $[71] = waveId;
    $[72] = websocketStatus;
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  connectedProfile?.id;
  drop.context_profile_context?.reaction;
  const handleClick = t18;
  let t19;
  if ($[74] !== reaction.profiles) {
    t19 = reaction.profiles.slice(0, 3);
    $[74] = reaction.profiles;
    $[75] = t19;
  } else {
    t19 = $[75];
  }
  const displayProfiles = t19;
  const moreCount = total > 3 ? total - 3 : 0;
  let t20;
  if ($[76] !== displayProfiles || $[77] !== moreCount) {
    t20 = {
      displayProfiles,
      moreCount
    };
    $[76] = displayProfiles;
    $[77] = moreCount;
    $[78] = t20;
  } else {
    t20 = $[78];
  }
  const tooltipProfiles = t20;
  let t21;
  if ($[79] !== isTouchDevice || $[80] !== onLoadDetails) {
    t21 = () => {
      if (!isTouchDevice) {
        onLoadDetails()?.catch(_temp5);
      }
    };
    $[79] = isTouchDevice;
    $[80] = onLoadDetails;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  const handlePointerEnter = t21;
  let t22;
  if ($[82] !== onOpenDetailDialog || $[83] !== reaction.reaction) {
    t22 = e_4 => {
      e_4.stopPropagation();
      onOpenDetailDialog(reaction.reaction);
    };
    $[82] = onOpenDetailDialog;
    $[83] = reaction.reaction;
    $[84] = t22;
  } else {
    t22 = $[84];
  }
  const handleMoreClick = t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  let t36;
  let tooltipContent;
  if ($[85] !== animate || $[86] !== canReact || $[87] !== emojiNode || $[88] !== handleClick || $[89] !== handleMoreClick || $[90] !== handlePointerEnter || $[91] !== isDetailsLoading || $[92] !== isTouchDevice || $[93] !== selected || $[94] !== tooltipId || $[95] !== tooltipProfiles.displayProfiles || $[96] !== tooltipProfiles.moreCount || $[97] !== total || $[98] !== wrappedTouchHandlers) {
    t36 = Symbol.for("react.early_return_sentinel");
    bb1: {
      const {
        animationStyle,
        bgStyle,
        borderStyle,
        hoverStyle
      } = getReactionClassNames({
        animate,
        canReact,
        selected
      });
      let t37;
      if ($[114] !== handleMoreClick || $[115] !== isDetailsLoading || $[116] !== tooltipProfiles.displayProfiles || $[117] !== tooltipProfiles.moreCount || $[118] !== total) {
        t37 = <ReactionTooltipContent displayProfiles={tooltipProfiles.displayProfiles} isDetailsLoading={isDetailsLoading} moreCount={tooltipProfiles.moreCount} onMoreClick={handleMoreClick} total={total} />;
        $[114] = handleMoreClick;
        $[115] = isDetailsLoading;
        $[116] = tooltipProfiles.displayProfiles;
        $[117] = tooltipProfiles.moreCount;
        $[118] = total;
        $[119] = t37;
      } else {
        t37 = $[119];
      }
      tooltipContent = t37;
      if (!emojiNode || total === 0) {
        t36 = null;
        break bb1;
      }
      t26 = "button";
      t27 = handleClick;
      t28 = !canReact;
      t29 = !canReact;
      t30 = handlePointerEnter;
      t31 = handlePointerEnter;
      if ($[120] !== isTouchDevice || $[121] !== tooltipId) {
        t32 = !isTouchDevice && {
          "data-tooltip-id": tooltipId
        };
        $[120] = isTouchDevice;
        $[121] = tooltipId;
        $[122] = t32;
      } else {
        t32 = $[122];
      }
      t33 = "true";
      t34 = clsx("tw-mt-1 tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-solid tw-px-2 tw-py-1 tw-shadow-sm", canReact ? "hover:tw-text-iron-100" : "tw-cursor-default", borderStyle, bgStyle, hoverStyle);
      t35 = wrappedTouchHandlers;
      t24 = "tw-flex tw-h-full tw-items-center tw-gap-x-1";
      if ($[123] !== emojiNode) {
        t25 = <div className="tw-flex tw-size-5 tw-flex-shrink-0 tw-items-center tw-justify-center">{emojiNode}</div>;
        $[123] = emojiNode;
        $[124] = t25;
      } else {
        t25 = $[124];
      }
      t23 = clsx("tw-min-w-[2ch] tw-text-xs tw-font-normal", animationStyle);
    }
    $[85] = animate;
    $[86] = canReact;
    $[87] = emojiNode;
    $[88] = handleClick;
    $[89] = handleMoreClick;
    $[90] = handlePointerEnter;
    $[91] = isDetailsLoading;
    $[92] = isTouchDevice;
    $[93] = selected;
    $[94] = tooltipId;
    $[95] = tooltipProfiles.displayProfiles;
    $[96] = tooltipProfiles.moreCount;
    $[97] = total;
    $[98] = wrappedTouchHandlers;
    $[99] = t23;
    $[100] = t24;
    $[101] = t25;
    $[102] = t26;
    $[103] = t27;
    $[104] = t28;
    $[105] = t29;
    $[106] = t30;
    $[107] = t31;
    $[108] = t32;
    $[109] = t33;
    $[110] = t34;
    $[111] = t35;
    $[112] = t36;
    $[113] = tooltipContent;
  } else {
    t23 = $[99];
    t24 = $[100];
    t25 = $[101];
    t26 = $[102];
    t27 = $[103];
    t28 = $[104];
    t29 = $[105];
    t30 = $[106];
    t31 = $[107];
    t32 = $[108];
    t33 = $[109];
    t34 = $[110];
    t35 = $[111];
    t36 = $[112];
    tooltipContent = $[113];
  }
  if (t36 !== Symbol.for("react.early_return_sentinel")) {
    return t36;
  }
  let t37;
  if ($[125] !== total) {
    t37 = formatLargeNumber(total);
    $[125] = total;
    $[126] = t37;
  } else {
    t37 = $[126];
  }
  let t38;
  if ($[127] !== t23 || $[128] !== t37) {
    t38 = <span className={t23}>{t37}</span>;
    $[127] = t23;
    $[128] = t37;
    $[129] = t38;
  } else {
    t38 = $[129];
  }
  let t39;
  if ($[130] !== t24 || $[131] !== t25 || $[132] !== t38) {
    t39 = <div className={t24}>{t25}{t38}</div>;
    $[130] = t24;
    $[131] = t25;
    $[132] = t38;
    $[133] = t39;
  } else {
    t39 = $[133];
  }
  let t40;
  if ($[134] !== t26 || $[135] !== t27 || $[136] !== t28 || $[137] !== t29 || $[138] !== t30 || $[139] !== t31 || $[140] !== t32 || $[141] !== t33 || $[142] !== t34 || $[143] !== t35 || $[144] !== t39) {
    t40 = <button type={t26} onClick={t27} disabled={t28} aria-disabled={t29} onMouseEnter={t30} onFocus={t31} {...t32} data-text-selection-exclude={t33} className={t34} {...t35}>{t39}</button>;
    $[134] = t26;
    $[135] = t27;
    $[136] = t28;
    $[137] = t29;
    $[138] = t30;
    $[139] = t31;
    $[140] = t32;
    $[141] = t33;
    $[142] = t34;
    $[143] = t35;
    $[144] = t39;
    $[145] = t40;
  } else {
    t40 = $[145];
  }
  let t41;
  if ($[146] !== emojiNodeTooltip || $[147] !== isTouchDevice || $[148] !== tooltipContent || $[149] !== tooltipId) {
    t41 = !isTouchDevice && <Tooltip id={tooltipId} delayShow={250} place="bottom" opacity={1} clickable={true} style={{
      backgroundColor: "#37373E",
      color: "white",
      zIndex: 50
    }}><div className="tw-flex tw-items-center tw-gap-2">{emojiNodeTooltip}{tooltipContent}</div></Tooltip>;
    $[146] = emojiNodeTooltip;
    $[147] = isTouchDevice;
    $[148] = tooltipContent;
    $[149] = tooltipId;
    $[150] = t41;
  } else {
    t41 = $[150];
  }
  let t42;
  if ($[151] !== t40 || $[152] !== t41) {
    t42 = <>{t40}{t41}</>;
    $[151] = t40;
    $[152] = t41;
    $[153] = t42;
  } else {
    t42 = $[153];
  }
  return t42;
}
function _temp5() {}
function _temp4(s_0) {
  return !s_0;
}
function _temp3(s) {
  return !s;
}
function _temp2(cat) {
  return cat.emojis;
}
export default WaveDropReactions;